import axios from 'axios';
import * as jose from 'jose';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  ReactElement,
} from 'react';
import { BASE_URL, getPublicEnv } from '../config/api';

interface AuthContextType {
  user: any | null;
  loading: boolean;
  signUp: (cpf: string, senha: string, extraData: any) => Promise<void>;
  signIn: (cpf: string, senha: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function safeLocalStorageSet(key: string, value: string) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch {
    console.warn(`localStorage.setItem falhou: ${key}`);
  }
}

function safeLocalStorageRemove(key: string) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  } catch {
    console.warn(`localStorage.removeItem falhou: ${key}`);
  }
}

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: BASE_URL,
  });

  useEffect(() => {
    setLoading(false);
  }, []);

  const signUp = async (email: string, senha: string, extraData: any) => {
    try {
      setLoading(true);
      const payload = { email, senha, ...extraData };
      const { data } = await api.post('/club/insert', payload);

      if (data?.message) throw new Error(data.message);

      if (data?.cpf) {
        setUser(data);
        safeLocalStorageSet('user', JSON.stringify(data));
        await enviarTokenClubeBen(data);
      }

      alert('Usuário criado com sucesso!');
    } catch (error: any) {
      console.error('Erro ao cadastrar usuário:', error);
      alert(error.response?.data?.mensagem || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (cpf: string, senha: string): Promise<void> => {
    try {
      setLoading(true);

      const cpfLimpo = cpf.replace(/\D/g, '');

      if (!cpfLimpo || cpfLimpo.length !== 11) {
        throw new Error('CPF deve ter 11 dígitos');
      }

      const { data } = await api.post('/club/login', { cpf: cpfLimpo, senha });

      if (data?.mensagem && data.sucesso === false) {
        throw new Error(data.mensagem);
      }

      if (!data?.usuario) {
        throw new Error('CPF não encontrado.');
      }

      const userData = data.usuario;

      setUser(userData);
      safeLocalStorageSet('user', JSON.stringify(userData));

      await enviarTokenClubeBen(userData);
    } catch (error: any) {
      console.error('Erro completo no login:', {
        message: error.message,
        response: error.response,
      });

      if (error.response) {
        const status = error.response.status;
        const apiMessage =
          error.response.data?.mensagem || error.response.data?.message;

        if (status === 404) {
          throw new Error(
            'CPF não cadastrado no sistema. Crie sua conta para continuar.',
          );
        }

        if (status === 401) {
          throw new Error('CPF ou senha incorretos. Verifique seus dados.');
        }

        if (apiMessage) throw new Error(apiMessage);

        throw new Error(`Erro ${status}: Não foi possível fazer login`);
      }

      throw new Error('Erro ao conectar com o servidor. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const enviarTokenClubeBen = async (userData: any) => {
    try {
      if (!userData.nome || !userData.email || !userData.cpf) {
        console.error('Dados insuficientes para gerar token:', userData);
        return;
      }

      const tokenPayload = {
        id: userData.id?.toString() || userData.cpf,
        nome: userData.nome,
        email: userData.email,
        sexo: userData.genero === 'M' ? 'M' : 'F',
        nascimento: userData.data_nascimento,
        clube_id: '261',
        cpf: userData.cpf?.replace(/\D/g, ''),
        utm_source: 'Site Odonto Group',
        status_email: userData.ofertas ? 1 : 0,
        status_sms: userData.mensagens ? 1 : 0,
        uf: userData.estado || userData.uf || 'DF',
      };

      const jwtSecret = getPublicEnv(
        'REACT_APP_CLUBE_BEN_SECRET',
        'bR7tX9pL2fQ4zM6n',
      );

      const secret = new TextEncoder().encode(jwtSecret);
      const token = await new jose.SignJWT(tokenPayload)
        .setProtectedHeader({ alg: 'HS256' })
        .sign(secret);

      const clubeUrl = 'https://cuidado.clubeben.com.br/auth/general/';

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = clubeUrl;
      form.style.display = 'none';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token';
      input.value = token;
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
    } catch (error: any) {
      console.error('ERRO ao gerar/enviar token:', error.message);
    }
  };

  const signOut = (): void => {
    setUser(null);
    safeLocalStorageRemove('user');
  };

  const value: AuthContextType = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
